import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/api';
import type { WarmupStatus, RebuildStatus, PlanCacheWarmupStatus } from '../lib/api';
import type { RegimeCacheStatusResponse } from '../types/api';
import { RefreshCw, Database, Trash2, AlertCircle, CheckCircle, BarChart3, Layers, Activity, Flame, Clock, Wrench, Cpu, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { CollapsibleCard } from '../components/CollapsibleCard';

interface SymbolCoverage {
  symbol: string;
  row_count: number;
  schwab_rows?: number;
  yfinance_rows?: number;
  source?: string;
  min_date: string | null;
  max_date: string | null;
}

interface VersionCoverage {
  version_id: number;
  symphony_db_id: number;
  symphony_name: string | null;
  version_hash: string | null;
  is_current: boolean;
  is_active: boolean;
  total_rows: number;
  stale_rows: number;
  min_weight_date: string | null;
  max_weight_date: string | null;
  code_length: number;
}

const REBUILD_MAX_CODE_BYTES = 28_500_000;

interface FillHistoryState {
  running: boolean;
  jobId: string | null;
  currentFrom: string | null;
  currentTo: string | null;
  chunksComputed: number;
  chunksSkipped: number;
  totalFilled: number;
  nextFrom: string | null;
  error: string | null;
  done: boolean;
}

interface ResultCacheStats {
  total_entries: number;
  stale_entries: number;
  fresh_entries: number;
  total_hits: number;
  session_hits: number;
  session_misses: number;
}

interface CacheStatus {
  price_cache: {
    symbols: SymbolCoverage[];
    total_symbols: number;
    total_rows: number;
  };
  weight_cache: {
    versions: VersionCoverage[];
    total_versions: number;
    total_rows: number;
    stale_rows: number;
  };
  backtest_result_cache: ResultCacheStats;
}

export function CacheAdmin() {
  const [status, setStatus] = useState<CacheStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [warmupStatus, setWarmupStatus] = useState<WarmupStatus | null>(null);
  const [warmupLoading, setWarmupLoading] = useState(true);
  const [warmupRunning, setWarmupRunning] = useState(false);
  const [warmupMessage, setWarmupMessage] = useState<string | null>(null);
  const [warmupError, setWarmupError] = useState<string | null>(null);

  const [rebuildStatus, setRebuildStatus] = useState<RebuildStatus | null>(null);
  const [rebuildLoading, setRebuildLoading] = useState(true);
  const [rebuildRunning, setRebuildRunning] = useState(false);
  const [rebuildMessage, setRebuildMessage] = useState<string | null>(null);
  const [rebuildError, setRebuildError] = useState<string | null>(null);

  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupMessage, setCleanupMessage] = useState<string | null>(null);
  const [cleanupError, setCleanupError] = useState<string | null>(null);

  const [planWarmupStatus, setPlanWarmupStatus] = useState<PlanCacheWarmupStatus | null>(null);
  const [planWarmupLoading, setPlanWarmupLoading] = useState(true);
  const [planWarmupRunning, setPlanWarmupRunning] = useState(false);
  const [planWarmupMessage, setPlanWarmupMessage] = useState<string | null>(null);
  const [planWarmupError, setPlanWarmupError] = useState<string | null>(null);
  const planWarmupPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Fill history state (per strategy) ---
  const [fillHistoryState, setFillHistoryState] = useState<Record<number, FillHistoryState>>({});
  // Tracks which symphony IDs currently have an active poller so we never start duplicates.
  const fillHistoryPollersRef = useRef<Set<number>>(new Set());

  // --- Regime cache state ---
  const [regimeCoverageStatus, setRegimeCoverageStatus] = useState<RegimeCacheStatusResponse | null>(null);
  const [regimeCoverageLoading, setRegimeCoverageLoading] = useState(false);
  const [regimeBackfillRunning, setRegimeBackfillRunning] = useState(false);
  const [regimeMessage, setRegimeMessage] = useState<string | null>(null);
  const [regimeError, setRegimeError] = useState<string | null>(null);
  const regimePollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const defaultRegimeStartDate = (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 5); return d.toISOString().split('T')[0]; })();
  const [regimeStartDate, setRegimeStartDate] = useState(defaultRegimeStartDate);
  const [regimeEndDate, setRegimeEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [regimeForceRebuild, setRegimeForceRebuild] = useState(false);

  const fetchRegimeCoverageStatus = useCallback(async () => {
    setRegimeCoverageLoading(true);
    try {
      const data = await api.getRegimeCacheStatus({ start_date: regimeStartDate, end_date: regimeEndDate });
      setRegimeCoverageStatus(data);
      if (data.job_status?.is_running) {
        setRegimeBackfillRunning(true);
      } else {
        setRegimeBackfillRunning(false);
      }
    } catch {
      // non-fatal
    } finally {
      setRegimeCoverageLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regimeStartDate, regimeEndDate]);

  const scheduleRegimePoll = useCallback(() => {
    if (regimePollRef.current) clearTimeout(regimePollRef.current);
    regimePollRef.current = setTimeout(async () => {
      try {
        const data = await api.getRegimeBackfillStatus();
        if (data.is_running) {
          scheduleRegimePoll();
        } else {
          setRegimeBackfillRunning(false);
          if (data.success === true) {
            setRegimeMessage(
              `Backfill complete: ${data.rows_created?.toLocaleString() ?? 0} rows created, ` +
              `${data.rows_skipped?.toLocaleString() ?? 0} skipped.`
            );
          } else if (data.success === false && data.error) {
            setRegimeError(`Backfill failed: ${data.error}`);
          }
          fetchRegimeCoverageStatus();
        }
      } catch {
        // keep polling on transient errors
        scheduleRegimePoll();
      }
    }, 3000);
  }, [fetchRegimeCoverageStatus]);

  // Stable ref so the reconnect useEffect can call the latest pollFillJob
  // without taking it as a dep (which would cause infinite re-runs).
  const pollFillJobRef = useRef<((id: number, jobId: string, name: string | null) => void) | null>(null);

  // How many consecutive poll failures before giving up and showing "Retry History".
  // Each retry waits POLL_RETRY_DELAY_MS, so MAX_POLL_FAILURES × delay must cover the
  // worst-case server stall (the AST eval for a 29 MB strategy holds the GIL for ~110 s).
  // 8 × 15 000 ms = 120 s — enough headroom.
  const MAX_POLL_FAILURES = 8;
  const POLL_RETRY_DELAY_MS = 15_000;

  const pollFillJob = useCallback(async (
    symphonyDbId: number,
    jobId: string,
    name: string | null,
    failureCount = 0,
  ) => {
    try {
      const poll = await api.cacheFillHistoryPoll(jobId);
      const isRunning = poll.status === 'running';
      const isFailed = poll.status === 'failed';
      const isDone = poll.done ?? false;

      setFillHistoryState(prev => ({
        ...prev,
        [symphonyDbId]: {
          running: isRunning,
          jobId,
          currentFrom: poll.current_from ?? null,
          currentTo: poll.current_to ?? null,
          chunksComputed: poll.chunks_computed ?? 0,
          chunksSkipped: poll.chunks_skipped ?? 0,
          totalFilled: poll.days_filled ?? 0,
          nextFrom: poll.next_fill_from ?? null,
          error: isFailed ? (poll.error || 'Fill failed') : null,
          done: isDone,
        },
      }));

      if (isRunning) {
        // Successful poll — reset failure count and schedule next poll at normal cadence.
        setTimeout(() => pollFillJob(symphonyDbId, jobId, name, 0), 4000);
      } else {
        fillHistoryPollersRef.current.delete(symphonyDbId);
        if (isDone) {
          const filled = poll.days_filled ?? 0;
          setActionMessage(
            filled > 0
              ? `History fill complete for "${name || symphonyDbId}": ${filled.toLocaleString()} trading days cached.`
              : `History fill complete for "${name || symphonyDbId}": no new days added (strategy history starts from earliest available data).`
          );
          fetchStatusRef.current?.();
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      const isJobGone = msg.includes('not found or expired');

      if (isJobGone) {
        // Job evicted — server was restarted. Auto-resume will kick in; show a clear message.
        fillHistoryPollersRef.current.delete(symphonyDbId);
        setFillHistoryState(prev => ({
          ...prev,
          [symphonyDbId]: {
            ...prev[symphonyDbId],
            running: false,
            error: 'Server restarted — fill will auto-resume shortly, or click Fill History',
          },
        }));
      } else if (failureCount < MAX_POLL_FAILURES) {
        // Transient failure — server is likely busy with a heavy Polars eval (can hold the
        // GIL for ~110 s on large strategies).  Keep the spinner and retry after a longer
        // delay rather than immediately surfacing "Retry History".
        setFillHistoryState(prev => ({
          ...prev,
          [symphonyDbId]: { ...prev[symphonyDbId], running: true },
        }));
        setTimeout(
          () => pollFillJob(symphonyDbId, jobId, name, failureCount + 1),
          POLL_RETRY_DELAY_MS,
        );
      } else {
        // Exhausted retries — genuinely unreachable.
        fillHistoryPollersRef.current.delete(symphonyDbId);
        setFillHistoryState(prev => ({
          ...prev,
          [symphonyDbId]: {
            ...prev[symphonyDbId],
            running: false,
            error: 'Server unavailable — click Retry History to reconnect',
          },
        }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the ref in sync so the reconnect effect always calls the latest version.
  pollFillJobRef.current = pollFillJob;

  // Stable ref for fetchStatus (defined below) so pollFillJob can call it
  // without a circular dependency.
  const fetchStatusRef = useRef<(() => void) | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getCacheStatus();
      setStatus(data as CacheStatus);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cache status');
    } finally {
      setLoading(false);
    }
  }, []);
  // Keep ref in sync so pollFillJob can call it without a dep cycle.
  fetchStatusRef.current = fetchStatus;

  const fetchWarmupStatus = useCallback(async () => {
    setWarmupLoading(true);
    try {
      const data = await api.getCacheWarmupStatus();
      setWarmupStatus(data);
    } catch {
      // non-fatal
    } finally {
      setWarmupLoading(false);
    }
  }, []);

  const fetchRebuildStatus = useCallback(async () => {
    setRebuildLoading(true);
    try {
      const data = await api.getCacheRebuildStatus();
      setRebuildStatus(data);
    } catch {
      // non-fatal
    } finally {
      setRebuildLoading(false);
    }
  }, []);

  const fetchPlanWarmupStatus = useCallback(async (): Promise<PlanCacheWarmupStatus | null> => {
    try {
      const data = await api.getPlanCacheWarmupStatus();
      setPlanWarmupStatus(data);
      return data;
    } catch {
      // non-fatal
      return null;
    } finally {
      setPlanWarmupLoading(false);
    }
  }, []);

  // Auto-poll plan warmup while it is running
  const schedulePlanWarmupPoll = useCallback(() => {
    if (planWarmupPollRef.current) clearTimeout(planWarmupPollRef.current);
    planWarmupPollRef.current = setTimeout(async () => {
      const data = await fetchPlanWarmupStatus();
      if (data?.is_running) {
        schedulePlanWarmupPoll();
      }
    }, 1500);
  }, [fetchPlanWarmupStatus]);

  const handleRegimeBackfill = async () => {
    setRegimeMessage(null);
    setRegimeError(null);
    setRegimeBackfillRunning(true);
    try {
      await api.triggerRegimeBackfill({
        start_date: regimeStartDate,
        end_date: regimeEndDate,
        force_rebuild: regimeForceRebuild,
      });
      scheduleRegimePoll();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start backfill';
      if (msg.includes('409') || msg.toLowerCase().includes('already running')) {
        setRegimeMessage('A regime backfill is already in progress — check back shortly.');
        scheduleRegimePoll();
      } else {
        setRegimeError(msg);
        setRegimeBackfillRunning(false);
      }
    }
  };

  const handleRegimeEodJob = async () => {
    setRegimeMessage(null);
    setRegimeError(null);
    try {
      await api.triggerRegimeEodJob();
      setRegimeMessage('Regime EOD job started in background. Refresh coverage in a few minutes.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start EOD job';
      if (msg.includes('422') || msg.toLowerCase().includes('disabled')) {
        setRegimeError('Regime engine is disabled. Set ENABLE_REGIME_ENGINE=true to use this feature.');
      } else if (msg.includes('409') || msg.toLowerCase().includes('already running')) {
        setRegimeMessage('A regime job is already running — check back shortly.');
      } else {
        setRegimeError(msg);
      }
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchWarmupStatus();
    fetchRebuildStatus();
    fetchPlanWarmupStatus().then((data) => {
      if (data?.is_running) schedulePlanWarmupPoll();
    });
    fetchRegimeCoverageStatus();
    return () => {
      if (planWarmupPollRef.current) clearTimeout(planWarmupPollRef.current);
      if (regimePollRef.current) clearTimeout(regimePollRef.current);
    };
  }, [fetchStatus, fetchWarmupStatus, fetchRebuildStatus, fetchPlanWarmupStatus, schedulePlanWarmupPoll, fetchRegimeCoverageStatus]);

  // Reconnect to any fill-history jobs that are still running on the server
  // after a page reload.  Runs whenever the weight-cache status table refreshes.
  useEffect(() => {
    if (!status) return;
    status.weight_cache.versions
      .filter(v => v.is_current && !fillHistoryPollersRef.current.has(v.symphony_db_id))
      .forEach(async (v) => {
        try {
          const active = await api.cacheFillHistoryActive(v.symphony_db_id);
          if (!active.active || !active.job_id) return;
          // Guard again inside the async callback — another iteration may have set it.
          if (fillHistoryPollersRef.current.has(v.symphony_db_id)) return;
          fillHistoryPollersRef.current.add(v.symphony_db_id);
          setFillHistoryState(prev => ({
            ...prev,
            [v.symphony_db_id]: {
              running: true,
              jobId: active.job_id!,
              currentFrom: active.current_from ?? null,
              currentTo: null,
              chunksComputed: active.chunks_computed ?? 0,
              chunksSkipped: active.chunks_skipped ?? 0,
              totalFilled: active.days_filled ?? 0,
              nextFrom: null,
              error: null,
              done: false,
            },
          }));
          pollFillJobRef.current?.(v.symphony_db_id, active.job_id!, v.symphony_name);
        } catch {
          // non-fatal — server may not have the endpoint yet on old deploys
        }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleRunWarmup = async () => {
    setWarmupRunning(true);
    setWarmupMessage(null);
    setWarmupError(null);
    try {
      await api.triggerCacheWarmup();
      // Poll every 5 s. Stop when ran_at changes OR is_running goes false after a run.
      const baseline = warmupStatus?.ran_at ?? null;
      let attempts = 0;
      const poll = async () => {
        attempts++;
        try {
          const data = await api.getCacheWarmupStatus();
          setWarmupStatus(data);
          const done = data.ran_at !== baseline || (!data.is_running && attempts > 1);
          if (done || attempts >= 90) {
            if (data.success) {
              setWarmupMessage(
                data.symbol_count
                  ? `Warm-up complete: ${data.symbol_count} symbol(s), ${data.total_rows?.toLocaleString()} price rows upserted.`
                  : 'Warm-up complete: no active strategy symbols found.'
              );
            } else if (data.ran_at !== baseline) {
              setWarmupError(data.error ?? 'Warm-up failed');
            }
            setWarmupRunning(false);
            await fetchStatus();
            return;
          }
        } catch {
          // ignore transient poll errors
        }
        setTimeout(poll, 5000);
      };
      setTimeout(poll, 5000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start warm-up';
      if (msg.includes('409') || msg.toLowerCase().includes('already running')) {
        setWarmupMessage('A warm-up is already in progress — check back shortly.');
      } else {
        setWarmupError(msg);
      }
      setWarmupRunning(false);
    }
  };

  const handleRunPlanWarmup = async () => {
    setPlanWarmupRunning(true);
    setPlanWarmupMessage(null);
    setPlanWarmupError(null);
    try {
      await api.triggerPlanCacheWarmup();
      // Self-contained polling loop: keeps going until is_running goes false,
      // then resets planWarmupRunning and sets a completion message.
      let attempts = 0;
      const poll = async () => {
        attempts++;
        try {
          const data = await fetchPlanWarmupStatus();
          if (data?.is_running && attempts < 120) {
            setTimeout(poll, 1500);
            return;
          }
          // Run finished (or timed out)
          setPlanWarmupRunning(false);
          if (data) {
            if (data.strategies_failed === 0) {
              setPlanWarmupMessage(
                `Warm-up complete: ${data.strategies_ok} strategy/strategies compiled in ${
                  data.elapsed_ms != null
                    ? data.elapsed_ms < 1000
                      ? `${Math.round(data.elapsed_ms)} ms`
                      : `${(data.elapsed_ms / 1000).toFixed(1)} s`
                    : '—'
                }.`
              );
            } else {
              setPlanWarmupMessage(
                `Warm-up finished with ${data.strategies_failed} error(s) — ${data.strategies_ok} compiled OK.`
              );
            }
          }
        } catch {
          // transient error — keep trying up to limit
          if (attempts < 120) setTimeout(poll, 1500);
          else setPlanWarmupRunning(false);
        }
      };
      setTimeout(poll, 1000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start plan cache warm-up';
      if (msg.includes('409') || msg.toLowerCase().includes('already running')) {
        setPlanWarmupMessage('A plan cache warm-up is already in progress — check back shortly.');
      } else {
        setPlanWarmupError(msg);
      }
      setPlanWarmupRunning(false);
    }
  };

  const handleRunRebuild = async () => {
    setRebuildRunning(true);
    setRebuildMessage(null);
    setRebuildError(null);
    try {
      await api.triggerCacheRebuild();
      const baseline = rebuildStatus?.ran_at ?? null;
      let attempts = 0;
      const poll = async () => {
        attempts++;
        try {
          const data = await api.getCacheRebuildStatus();
          setRebuildStatus(data);
          const done = data.ran_at !== baseline || (!data.is_running && attempts > 1);
          if (done || attempts >= 90) {
            if (data.success) {
              setRebuildMessage(
                `Rebuild complete: ${data.strategies_processed ?? 0} strategy/strategies, ` +
                `${(data.weight_dates_filled ?? 0).toLocaleString()} weight dates filled, ` +
                `${data.backtests_cached ?? 0} backtest(s) cached.`
              );
            } else if (data.ran_at !== baseline) {
              setRebuildError(data.error ?? 'Rebuild failed');
            }
            setRebuildRunning(false);
            await fetchStatus();
            return;
          }
        } catch {
          // ignore transient poll errors
        }
        setTimeout(poll, 5000);
      };
      setTimeout(poll, 5000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start rebuild';
      if (msg.includes('409') || msg.toLowerCase().includes('already running')) {
        setRebuildMessage('A rebuild is already in progress — check back shortly.');
      } else {
        setRebuildError(msg);
      }
      setRebuildRunning(false);
    }
  };

  const handleRefreshPrices = async () => {
    setBusy(true);
    setActionMessage(null);
    setActionError(null);
    try {
      await api.cacheRefreshPrices();
      setActionMessage('Price refresh started in background. Reload in a few minutes to see updated coverage.');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start price refresh');
    } finally {
      setBusy(false);
    }
  };

  const handleRebuildWeights = async (symphonyDbId: number, name: string | null) => {
    setBusy(true);
    setActionMessage(null);
    setActionError(null);
    try {
      await api.cacheRebuildWeights(symphonyDbId);
      setActionMessage(`Weight rebuild started for "${name || symphonyDbId}" in background.`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start weight rebuild');
    } finally {
      setBusy(false);
    }
  };

  const handleCleanupOrphaned = async () => {
    if (!window.confirm('This will permanently delete all weight cache data for deleted strategies. Continue?')) return;
    setCleanupBusy(true);
    setCleanupMessage(null);
    setCleanupError(null);
    try {
      const result = await api.cacheCleanupOrphaned();
      setCleanupMessage(
        result.versions_deleted === 0
          ? 'No orphaned cache data found.'
          : `Cleaned up ${result.versions_deleted} deleted strategy version(s): ` +
            `${result.weight_rows_deleted.toLocaleString()} weight rows, ` +
            `${result.backtest_result_rows_deleted} backtest result(s) removed.`
      );
      await fetchStatus();
    } catch (err) {
      setCleanupError(err instanceof Error ? err.message : 'Cleanup failed');
    } finally {
      setCleanupBusy(false);
    }
  };

  const handleFillHistory = async (symphonyDbId: number, name: string | null) => {
    // Don't start a duplicate if one is already running.
    if (fillHistoryPollersRef.current.has(symphonyDbId)) return;

    setFillHistoryState(prev => ({
      ...prev,
      [symphonyDbId]: {
        running: true, jobId: null, currentFrom: null, currentTo: null,
        chunksComputed: 0, chunksSkipped: 0, totalFilled: 0,
        nextFrom: null, error: null, done: false,
      },
    }));

    try {
      // Single POST — the server now runs ALL chunks in one background thread.
      const { job_id } = await api.cacheFillHistoryStart(symphonyDbId, null, 90);
      setFillHistoryState(prev => ({
        ...prev,
        [symphonyDbId]: { ...prev[symphonyDbId], jobId: job_id },
      }));
      fillHistoryPollersRef.current.add(symphonyDbId);
      // First poll after 4 s.
      setTimeout(() => pollFillJob(symphonyDbId, job_id, name), 4000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start fill';
      setFillHistoryState(prev => ({
        ...prev,
        [symphonyDbId]: { ...prev[symphonyDbId], running: false, error: msg },
      }));
      setActionError(msg);
    }
  };

  const handleClearResults = async () => {
    if (!confirm('Delete all backtest result cache entries? This cannot be undone.')) return;
    setBusy(true);
    setActionMessage(null);
    setActionError(null);
    try {
      const res = await api.cacheClearResults();
      setActionMessage(res.message || 'Backtest result cache cleared.');
      await fetchStatus();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to clear result cache');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Cache Admin</h1>
          <p className="text-gray-600 mt-1 text-base">
            Monitor and manage the backtest persistent caching layer
          </p>
        </div>
        <button
          onClick={() => { fetchStatus(); fetchWarmupStatus(); fetchRebuildStatus(); fetchPlanWarmupStatus(); }}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Markov Regime Score Cache card */}
      <CollapsibleCard title="Markov Regime Score Cache" icon={<Activity className="w-5 h-5 text-indigo-600" />}>
        <div className="p-0">
          <RegimeCacheCard
            coverage={regimeCoverageStatus}
            loading={regimeCoverageLoading}
            running={regimeBackfillRunning}
            message={regimeMessage}
            error={regimeError}
            startDate={regimeStartDate}
            endDate={regimeEndDate}
            forceRebuild={regimeForceRebuild}
            onStartDateChange={setRegimeStartDate}
            onEndDateChange={setRegimeEndDate}
            onForceRebuildChange={setRegimeForceRebuild}
            onRunBackfill={handleRegimeBackfill}
            onRunEod={handleRegimeEodJob}
            onRefreshCoverage={fetchRegimeCoverageStatus}
          />
        </div>
      </CollapsibleCard>

      {/* Plan cache warm-up progress card */}
      <CollapsibleCard title="Plan Cache Warmup" icon={<Flame className="w-5 h-5 text-orange-500" />}>
        <div className="p-0">
          <PlanCacheWarmupCard
            status={planWarmupStatus}
            loading={planWarmupLoading}
            running={planWarmupRunning}
            message={planWarmupMessage}
            error={planWarmupError}
            onRunNow={handleRunPlanWarmup}
          />
        </div>
      </CollapsibleCard>

      {/* Warm-up status card */}
      <CollapsibleCard title="Price Cache Warmup" icon={<Database className="w-5 h-5 text-emerald-600" />}>
        <div className="p-0">
          <WarmupCard
            status={warmupStatus}
            loading={warmupLoading}
            running={warmupRunning}
            message={warmupMessage}
            error={warmupError}
            onRunNow={handleRunWarmup}
          />
        </div>
      </CollapsibleCard>

      {/* Weight + backtest rebuild card */}
      <CollapsibleCard title="Weight & Backtest Rebuild" icon={<Wrench className="w-5 h-5 text-gray-600" />}>
        <div className="p-0">
          <RebuildCard
            status={rebuildStatus}
            loading={rebuildLoading}
            running={rebuildRunning}
            message={rebuildMessage}
            error={rebuildError}
            onRunNow={handleRunRebuild}
          />
        </div>
      </CollapsibleCard>

      {actionMessage && (
        <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-800">
          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
          {actionMessage}
        </div>
      )}
      {actionError && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600" />
          {actionError}
        </div>
      )}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600" />
          {error}
        </div>
      )}

      {loading && !status && (
        <div className="flex items-center justify-center h-40 text-gray-500 text-sm">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          Loading cache status…
        </div>
      )}

      {status && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <SummaryCard
              icon={<Database className="w-5 h-5" />}
              title="Price Cache"
              value={status.price_cache.total_rows.toLocaleString()}
              subtitle={`${status.price_cache.total_symbols} symbols`}
              color="emerald"
            />
            <SummaryCard
              icon={<Layers className="w-5 h-5" />}
              title="Weight Cache"
              value={status.weight_cache.total_rows.toLocaleString()}
              subtitle={`${status.weight_cache.stale_rows} stale rows`}
              color="blue"
            />
            <SummaryCard
              icon={<Activity className="w-5 h-5" />}
              title="Result Cache"
              value={status.backtest_result_cache.fresh_entries.toLocaleString()}
              subtitle={`${status.backtest_result_cache.total_hits} total hits`}
              color="purple"
            />
          </div>

          {/* Price Cache section */}
          <CollapsibleCard
            title="Price Cache"
            icon={<Database className="w-5 h-5 text-emerald-600" />}
            headerExtra={
              <button
                onClick={(e) => { e.stopPropagation(); handleRefreshPrices(); }}
                disabled={busy}
                className="ml-3 flex items-center gap-1.5 px-3 py-1 text-sm font-semibold rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            }
          >
            <div className="p-6">
            {status.price_cache.symbols.length === 0 ? (
              <p className="text-gray-500 text-sm">No price data cached yet. Run a backtest or click "Refresh All Prices".</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Symbol</th>
                      <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Source</th>
                      <th className="text-right py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rows</th>
                      <th className="text-right py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">From</th>
                      <th className="text-right py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">To</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {status.price_cache.symbols.slice(0, 50).map((s) => (
                      <tr key={s.symbol} className="hover:bg-gray-50">
                        <td className="py-2 pr-4 font-medium text-gray-900">{s.symbol}</td>
                        <td className="py-2 pr-4">
                          {s.source === 'schwab_3:51pm' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              Schwab 3:51PM
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                              yFinance EOD
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-right text-gray-600">{s.row_count.toLocaleString()}</td>
                        <td className="py-2 pr-4 text-right text-gray-500 text-xs">{s.min_date ?? '—'}</td>
                        <td className="py-2 text-right text-gray-500 text-xs">{s.max_date ?? '—'}</td>
                      </tr>
                    ))}
                    {status.price_cache.symbols.length > 50 && (
                      <tr>
                        <td colSpan={5} className="py-2 text-center text-xs text-gray-400">
                          … and {status.price_cache.symbols.length - 50} more symbols
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          </CollapsibleCard>

          {/* Weight Cache section */}
          {(() => {
            const orphanedVersions = status.weight_cache.versions.filter((v) => !v.is_active);
            const activeVersions = status.weight_cache.versions.filter((v) => v.is_current && v.is_active);
            return (
          <CollapsibleCard
            title="Weight Cache — Strategy Versions"
            icon={<Layers className="w-5 h-5 text-blue-600" />}
            headerExtra={orphanedVersions.length > 0 ? (
              <button
                onClick={(e) => { e.stopPropagation(); handleCleanupOrphaned(); }}
                disabled={cleanupBusy}
                className="ml-3 flex items-center gap-1.5 px-3 py-1 text-sm font-semibold rounded-lg border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50 transition-colors"
                title={`${orphanedVersions.length} version(s) from deleted strategies are taking up cache space`}
              >
                <Trash2 className="w-3.5 h-3.5" />
                {cleanupBusy ? 'Cleaning…' : `Cleanup ${orphanedVersions.length} Orphaned`}
              </button>
            ) : undefined}
          >
            <div className="p-6">
            {cleanupMessage && (
              <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                {cleanupMessage}
              </div>
            )}
            {cleanupError && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                {cleanupError}
              </div>
            )}
            {activeVersions.length === 0 ? (
              <p className="text-gray-500 text-sm">No strategy versions found. Add a DSL strategy to populate.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Strategy</th>
                      <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Version</th>
                      <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date Range</th>
                      <th className="text-right py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Days Cached</th>
                      <th className="text-right py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Stale</th>
                      <th className="text-right py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {activeVersions.map((v) => {
                      const fhs = fillHistoryState[v.symphony_db_id];
                      return (
                      <tr key={v.version_id} className="hover:bg-gray-50">
                        <td className="py-2.5 pr-4 font-medium text-gray-900 max-w-[180px] truncate">
                          {v.symphony_name || `Strategy ${v.symphony_db_id}`}
                        </td>
                        <td className="py-2.5 pr-4 font-mono text-xs text-gray-500">{v.version_hash ?? '—'}</td>
                        <td className="py-2.5 pr-4 text-xs text-gray-500">
                          {v.min_weight_date && v.max_weight_date
                            ? <span>{v.min_weight_date} → {v.max_weight_date}</span>
                            : <span className="text-gray-400">no cache</span>}
                        </td>
                        <td className="py-2.5 pr-4 text-right text-gray-600">{v.total_rows.toLocaleString()}</td>
                        <td className="py-2.5 pr-4 text-right">
                          {v.stale_rows > 0 ? (
                            <span className="text-amber-600 font-medium">{v.stale_rows}</span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                        <td className="py-2.5 text-right">
                          <div className="flex items-center justify-end gap-3">
                            {fhs?.running ? (
                              <span className="text-xs text-indigo-600 flex items-center gap-1">
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                {fhs.currentFrom ? fhs.currentFrom.slice(0, 7) : '…'} · {fhs.totalFilled.toLocaleString()} days
                              </span>
                            ) : fhs?.done ? (
                              <span className="text-xs text-green-600 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                {fhs.totalFilled > 0
                                  ? `${fhs.totalFilled.toLocaleString()} days filled`
                                  : v.min_weight_date
                                    ? `Starts ${v.min_weight_date}`
                                    : 'No earlier data'}
                              </span>
                            ) : fhs?.error ? (
                              <button
                                onClick={() => handleFillHistory(v.symphony_db_id, v.symphony_name)}
                                className="text-xs font-medium text-red-600 hover:text-red-800"
                                title={fhs.error}
                              >
                                Retry History
                              </button>
                            ) : (
                              <button
                                onClick={() => handleFillHistory(v.symphony_db_id, v.symphony_name)}
                                disabled={busy}
                                className="text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                                title="Fill historical weight cache (90-day chunks, safe for large strategies)"
                              >
                                Fill History
                              </button>
                            )}
                            {v.code_length > REBUILD_MAX_CODE_BYTES ? (
                              <span
                                className="text-xs text-gray-400 cursor-default"
                                title={`Strategy is ${(v.code_length / 1_000_000).toFixed(0)} MB — too large to rebuild in one shot. Use Fill History instead.`}
                              >
                                Rebuild unavailable
                              </span>
                            ) : (
                              <button
                                onClick={() => handleRebuildWeights(v.symphony_db_id, v.symphony_name)}
                                disabled={busy || fhs?.running}
                                className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                              >
                                Rebuild
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          </CollapsibleCard>
          );})()}

          {/* Backtest Result Cache section */}
          <CollapsibleCard
            title="Backtest Result Cache"
            icon={<BarChart3 className="w-5 h-5 text-purple-600" />}
            headerExtra={
              <button
                onClick={(e) => { e.stopPropagation(); handleClearResults(); }}
                disabled={busy || status.backtest_result_cache.total_entries === 0}
                className="ml-3 flex items-center gap-1.5 px-3 py-1 text-sm font-semibold rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Clear
              </button>
            }
          >
            <div className="p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <StatCell label="Fresh Entries" value={status.backtest_result_cache.fresh_entries} />
              <StatCell label="Stale Entries" value={status.backtest_result_cache.stale_entries} />
              <StatCell label="Total Hits" value={status.backtest_result_cache.total_hits} />
              <StatCell label="Session Hits" value={status.backtest_result_cache.session_hits} highlight />
              <StatCell label="Session Misses" value={status.backtest_result_cache.session_misses} />
              <StatCell
                label="Hit Rate (session)"
                value={
                  status.backtest_result_cache.session_hits + status.backtest_result_cache.session_misses > 0
                    ? `${Math.round(
                        (status.backtest_result_cache.session_hits /
                          (status.backtest_result_cache.session_hits + status.backtest_result_cache.session_misses)) *
                          100
                      )}%`
                    : '—'
                }
                highlight
              />
            </div>
            </div>
          </CollapsibleCard>
        </>
      )}
    </div>
  );
}

function PlanCacheWarmupCard({
  status,
  loading,
  running,
  message,
  error,
  onRunNow,
}: {
  status: PlanCacheWarmupStatus | null;
  loading: boolean;
  running: boolean;
  message: string | null;
  error: string | null;
  onRunNow: () => void;
}) {
  const neverRan = !status || (status.started_at === null && !status.is_running);
  const isRunning = status?.is_running === true;
  const hasFinished = !isRunning && status?.finished_at !== null && status?.started_at !== null;
  const hasFailed = hasFinished && (status?.strategies_failed ?? 0) > 0;

  const progress =
    status && status.strategies_total > 0
      ? Math.round((status.strategies_processed / status.strategies_total) * 100)
      : isRunning
      ? 0
      : 100;

  const formatTimestamp = (iso: string) => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const formatMs = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)} ms`;
    return `${(ms / 1000).toFixed(1)} s`;
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Cpu className="w-5 h-5 text-violet-500" />
            Plan Cache Warm-up
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Pre-compiles strategy ASTs at startup to eliminate cold-start latency
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isRunning && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold">
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              Running…
            </span>
          )}
          {hasFinished && !hasFailed && !isRunning && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
              <CheckCircle className="w-3.5 h-3.5" />
              Complete
            </span>
          )}
          {hasFinished && hasFailed && !isRunning && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
              <AlertCircle className="w-3.5 h-3.5" />
              Finished with errors
            </span>
          )}
          <button
            onClick={onRunNow}
            disabled={isRunning || running}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isRunning || running ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                Running…
              </>
            ) : (
              <>
                <Flame className="w-3.5 h-3.5" />
                Run Now
              </>
            )}
          </button>
        </div>
      </div>

      {loading && !status && (
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading warm-up status…
        </div>
      )}

      {!loading && neverRan && (
        <p className="text-gray-500 text-sm">
          No plan cache warm-up has run yet this session. It runs automatically at startup when strategies are available.
        </p>
      )}

      {message && (
        <div className="flex items-start gap-2 p-3 mb-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
          {message}
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2 p-3 mb-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600" />
          {error}
        </div>
      )}

      {(isRunning || hasFinished) && status && (
        <div className="space-y-4">
          {/* Progress bar — shown while running or just after completion */}
          {(isRunning || (hasFinished && status.strategies_total > 0)) && (
            <div>
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                <span>
                  {isRunning
                    ? status.current_symphony_id
                      ? `Compiling strategy ${status.current_symphony_id}…`
                      : 'Compiling strategies…'
                    : 'Done'}
                </span>
                <span className="font-medium text-gray-700">
                  {status.strategies_processed} / {status.strategies_total}
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                <div
                  className={`h-2.5 rounded-full transition-all duration-500 ${
                    hasFailed ? 'bg-amber-500' : 'bg-violet-500'
                  }`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">{progress}% complete</p>
            </div>
          )}

          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-lg p-3 bg-gray-50">
              <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
                <Clock className="w-3.5 h-3.5" />
                Started
              </div>
              <p className="text-sm font-semibold text-gray-900">
                {status.started_at ? formatTimestamp(status.started_at) : '—'}
              </p>
            </div>
            <div className={`rounded-lg p-3 ${status.strategies_ok > 0 ? 'bg-emerald-50 border border-emerald-100' : 'bg-gray-50'}`}>
              <p className="text-xs font-medium text-gray-500 mb-1">Compiled OK</p>
              <p className={`text-2xl font-bold ${status.strategies_ok > 0 ? 'text-emerald-700' : 'text-gray-700'}`}>
                {status.strategies_ok}
              </p>
            </div>
            <div className={`rounded-lg p-3 ${status.strategies_failed > 0 ? 'bg-red-50 border border-red-100' : 'bg-gray-50'}`}>
              <p className="text-xs font-medium text-gray-500 mb-1">Failed</p>
              <p className={`text-2xl font-bold ${status.strategies_failed > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                {status.strategies_failed}
              </p>
            </div>
            <div className={`rounded-lg p-3 ${status.elapsed_ms != null ? 'bg-violet-50 border border-violet-100' : 'bg-gray-50'}`}>
              <p className="text-xs font-medium text-gray-500 mb-1">Total Time</p>
              <p className={`text-2xl font-bold ${status.elapsed_ms != null ? 'text-violet-700' : 'text-gray-400'}`}>
                {status.elapsed_ms != null ? formatMs(status.elapsed_ms) : '…'}
              </p>
            </div>
          </div>

          {/* Last error detail */}
          {status.last_error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800 font-mono break-all">
              {status.last_error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  icon,
  title,
  value,
  subtitle,
  color,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  subtitle: string;
  color: 'emerald' | 'blue' | 'purple';
}) {
  const colorMap = {
    emerald: 'text-emerald-600 bg-emerald-50',
    blue: 'text-blue-600 bg-blue-50',
    purple: 'text-purple-600 bg-purple-50',
  };
  return (
    <div className="bg-white rounded-lg p-5 shadow-md border border-gray-200">
      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${colorMap[color]} mb-3`}>
        {icon}
      </div>
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
    </div>
  );
}

function WarmupCard({
  status,
  loading,
  running,
  message,
  error,
  onRunNow,
}: {
  status: WarmupStatus | null;
  loading: boolean;
  running: boolean;
  message: string | null;
  error: string | null;
  onRunNow: () => void;
}) {
  const neverRan = !status || status.ran_at === null;
  const succeeded = status?.success === true;
  const failed = status?.success === false;
  const serverRunning = status?.is_running === true;

  const formatTimestamp = (iso: string) => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-500" />
          Last Cache Warm-up
        </h2>
        <button
          onClick={onRunNow}
          disabled={running || serverRunning}
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 transition-colors"
        >
          <Flame className={`w-4 h-4 ${(running || serverRunning) ? 'animate-pulse' : ''}`} />
          {running ? 'Running…' : serverRunning ? 'In Progress…' : 'Run Now'}
        </button>
      </div>

      {message && (
        <div className="flex items-start gap-3 p-3 mb-4 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
          {message}
        </div>
      )}
      {error && (
        <div className="flex items-start gap-3 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600" />
          {error}
        </div>
      )}

      {loading && !status && (
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading warm-up status…
        </div>
      )}

      {!loading && neverRan && (
        <p className="text-gray-500 text-sm">
          No warm-up has run yet. The nightly job runs at 2:00 AM EST, or click "Run Now" to warm the cache immediately.
        </p>
      )}

      {!neverRan && status && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-lg p-4 bg-gray-50">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
              <Clock className="w-3.5 h-3.5" />
              Last Run
            </div>
            <p className="text-sm font-semibold text-gray-900">
              {status.ran_at ? formatTimestamp(status.ran_at) : '—'}
            </p>
            <p className="text-xs mt-1">
              {failed ? (
                <span className="text-red-600 font-medium">Failed</span>
              ) : (
                <span className="text-emerald-600 font-medium">Succeeded</span>
              )}
            </p>
          </div>
          <div className={`rounded-lg p-4 ${succeeded ? 'bg-orange-50 border border-orange-100' : 'bg-gray-50'}`}>
            <p className="text-xs font-medium text-gray-500 mb-1">Symbols Refreshed</p>
            <p className={`text-2xl font-bold ${succeeded ? 'text-orange-700' : 'text-gray-900'}`}>
              {status.symbol_count ?? '—'}
            </p>
          </div>
          <div className={`rounded-lg p-4 ${succeeded ? 'bg-orange-50 border border-orange-100' : 'bg-gray-50'}`}>
            <p className="text-xs font-medium text-gray-500 mb-1">Price Rows Upserted</p>
            <p className={`text-2xl font-bold ${succeeded ? 'text-orange-700' : 'text-gray-900'}`}>
              {status.total_rows != null ? status.total_rows.toLocaleString() : '—'}
            </p>
          </div>
        </div>
      )}

      {!neverRan && status && failed && status.error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800 font-mono break-all">
          {status.error}
        </div>
      )}

      {!neverRan && status && succeeded && status.symbols.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium text-gray-500 mb-1.5">Symbols</p>
          <div className="flex flex-wrap gap-1.5">
            {status.symbols.map((sym) => (
              <span key={sym} className="px-2 py-0.5 text-xs bg-orange-100 text-orange-800 rounded-full font-medium">
                {sym}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RebuildCard({
  status,
  loading,
  running,
  message,
  error,
  onRunNow,
}: {
  status: RebuildStatus | null;
  loading: boolean;
  running: boolean;
  message: string | null;
  error: string | null;
  onRunNow: () => void;
}) {
  const neverRan = !status || status.ran_at === null;
  const succeeded = status?.success === true;
  const failed = status?.success === false;
  const serverRunning = status?.is_running === true;

  const formatTimestamp = (iso: string) => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-blue-500" />
            Weight &amp; Backtest Cache Rebuild
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Fills weight history and pre-warms backtest results for all active strategies. Nightly at 3:00 AM EST.
          </p>
        </div>
        <button
          onClick={onRunNow}
          disabled={running || serverRunning}
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          <Wrench className={`w-4 h-4 ${(running || serverRunning) ? 'animate-pulse' : ''}`} />
          {running ? 'Running…' : serverRunning ? 'In Progress…' : 'Run Now'}
        </button>
      </div>

      {message && (
        <div className="flex items-start gap-3 p-3 mb-4 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
          {message}
        </div>
      )}
      {error && (
        <div className="flex items-start gap-3 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600" />
          {error}
        </div>
      )}

      {loading && !status && (
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading rebuild status…
        </div>
      )}

      {!loading && neverRan && (
        <p className="text-gray-500 text-sm">
          No rebuild has run this session. The nightly job runs at 3:00 AM EST (after the 2:00 AM price warm-up), or click "Run Now" to rebuild immediately.
          <span className="block mt-1 text-xs text-gray-400">Note: rebuild status resets on server restart.</span>
        </p>
      )}

      {!neverRan && status && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="rounded-lg p-4 bg-gray-50 col-span-2 sm:col-span-1">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
              <Clock className="w-3.5 h-3.5" />
              Last Run
            </div>
            <p className="text-sm font-semibold text-gray-900">
              {status.ran_at ? formatTimestamp(status.ran_at) : '—'}
            </p>
            <p className="text-xs mt-1">
              {failed ? (
                <span className="text-red-600 font-medium">Failed</span>
              ) : (
                <span className="text-emerald-600 font-medium">Succeeded</span>
              )}
            </p>
          </div>
          <div className={`rounded-lg p-4 ${succeeded ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50'}`}>
            <p className="text-xs font-medium text-gray-500 mb-1">Strategies</p>
            <p className={`text-2xl font-bold ${succeeded ? 'text-blue-700' : 'text-gray-900'}`}>
              {status.strategies_processed ?? '—'}
            </p>
          </div>
          <div className={`rounded-lg p-4 ${succeeded ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50'}`}>
            <p className="text-xs font-medium text-gray-500 mb-1">Weight Dates Filled</p>
            <p className={`text-2xl font-bold ${succeeded ? 'text-blue-700' : 'text-gray-900'}`}>
              {status.weight_dates_filled != null ? status.weight_dates_filled.toLocaleString() : '—'}
            </p>
          </div>
          <div className={`rounded-lg p-4 ${succeeded ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50'}`}>
            <p className="text-xs font-medium text-gray-500 mb-1">Backtests Cached</p>
            <p className={`text-2xl font-bold ${succeeded ? 'text-blue-700' : 'text-gray-900'}`}>
              {status.backtests_cached ?? '—'}
            </p>
          </div>
        </div>
      )}

      {!neverRan && status && failed && status.error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800 font-mono break-all">
          {status.error}
        </div>
      )}

      {!neverRan && status && status.errors && status.errors.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium text-gray-500 mb-1.5">Errors ({status.errors.length})</p>
          <div className="space-y-1">
            {status.errors.slice(0, 5).map((e, i) => (
              <div key={i} className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 font-mono break-all">
                {e}
              </div>
            ))}
            {status.errors.length > 5 && (
              <p className="text-xs text-gray-400">… and {status.errors.length - 5} more</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCell({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string | number;
  highlight?: boolean;
}) {
  return (
    <div className={`rounded-lg p-4 ${highlight ? 'bg-emerald-50 border border-emerald-100' : 'bg-gray-50'}`}>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className={`text-xl font-bold mt-1 ${highlight ? 'text-emerald-700' : 'text-gray-900'}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Markov Regime Score Cache Card
// ---------------------------------------------------------------------------

interface RegimeCacheCardProps {
  coverage: RegimeCacheStatusResponse | null;
  loading: boolean;
  running: boolean;
  message: string | null;
  error: string | null;
  startDate: string;
  endDate: string;
  forceRebuild: boolean;
  onStartDateChange: (v: string) => void;
  onEndDateChange: (v: string) => void;
  onForceRebuildChange: (v: boolean) => void;
  onRunBackfill: () => void;
  onRunEod: () => void;
  onRefreshCoverage: () => void;
}

function RegimeCacheCard({
  coverage,
  loading,
  running,
  message,
  error,
  startDate,
  endDate,
  forceRebuild,
  onStartDateChange,
  onEndDateChange,
  onForceRebuildChange,
  onRunBackfill,
  onRunEod,
  onRefreshCoverage,
}: RegimeCacheCardProps) {
  const [symbolsExpanded, setSymbolsExpanded] = useState(false);

  const coveragePct = coverage?.coverage_pct ?? null;
  const pctColor =
    coveragePct == null ? 'text-gray-400'
    : coveragePct >= 80 ? 'text-teal-700'
    : coveragePct >= 40 ? 'text-amber-600'
    : 'text-red-600';

  const progressColor =
    coveragePct == null ? 'bg-gray-200'
    : coveragePct >= 80 ? 'bg-teal-500'
    : coveragePct >= 40 ? 'bg-amber-400'
    : 'bg-red-500';

  return (
    <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
      {/* Title row */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-50 rounded-lg">
            <TrendingUp className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">Markov Regime Score Cache</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              CONFIRMED_EOD daily scores for all active strategy symbols
            </p>
          </div>
        </div>
        <button
          onClick={onRefreshCoverage}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh Coverage
        </button>
      </div>

      {/* Coverage summary */}
      {coverage ? (
        <div className="mb-5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-500">
              {coverage.existing_rows.toLocaleString()} / {coverage.expected_rows.toLocaleString()} rows
              · {coverage.symbols_total} symbol{coverage.symbols_total !== 1 ? 's' : ''}
              · {coverage.configs_total} config{coverage.configs_total !== 1 ? 's' : ''}
            </span>
            <span className={`text-sm font-bold ${pctColor}`}>
              {coveragePct?.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className={`${progressColor} h-2 rounded-full transition-all duration-500`}
              style={{ width: `${Math.min(100, coveragePct ?? 0)}%` }}
            />
          </div>
          {(coverage.min_score_date || coverage.max_score_date) && (
            <p className="text-xs text-gray-400 mt-1.5">
              Score range: {coverage.min_score_date ?? '—'} → {coverage.max_score_date ?? '—'}
              · Coverage window: {coverage.start_date} → {coverage.end_date}
            </p>
          )}
        </div>
      ) : (
        <div className="mb-5">
          <p className="text-xs text-gray-400 italic">
            {loading ? 'Loading coverage…' : 'Click "Refresh Coverage" to check regime score cache.'}
          </p>
        </div>
      )}

      {/* Running progress */}
      {running && coverage?.job_status && (
        <div className="mb-4 p-3 bg-teal-50 border border-teal-100 rounded-lg">
          <div className="flex items-center gap-2 mb-1.5">
            <RefreshCw className="w-3.5 h-3.5 text-teal-600 animate-spin" />
            <span className="text-xs font-medium text-teal-700">Backfill in progress…</span>
          </div>
          {coverage.job_status.current_symbol && (
            <p className="text-xs text-teal-600 font-mono">→ {coverage.job_status.current_symbol}</p>
          )}
          <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-teal-700">
            <span>{coverage.job_status.symbols_done}/{coverage.job_status.symbols_total} symbols</span>
            <span>{(coverage.job_status.rows_created ?? 0).toLocaleString()} created</span>
            <span>{(coverage.job_status.rows_skipped ?? 0).toLocaleString()} skipped</span>
          </div>
        </div>
      )}

      {/* Success / error feedback */}
      {message && (
        <div className="mb-4 flex items-start gap-2 p-3 bg-teal-50 border border-teal-100 rounded-lg text-xs text-teal-800">
          <CheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-teal-600" />
          {message}
        </div>
      )}
      {error && (
        <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-red-500" />
          {error}
        </div>
      )}

      {/* Controls */}
      <div className="border border-gray-100 rounded-lg p-4 bg-gray-50 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 flex-wrap">
          {/* Force rebuild checkbox */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={forceRebuild}
              onChange={(e) => onForceRebuildChange(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-400"
            />
            <span className="text-xs text-gray-700 font-medium">Force rebuild</span>
            {forceRebuild && (
              <span className="text-xs text-amber-600 font-medium">(will re-compute existing rows)</span>
            )}
          </label>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={onRunEod}
              disabled={running}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-teal-300 text-teal-700 bg-teal-50 hover:bg-teal-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Activity className="w-3.5 h-3.5" />
              Run Latest Day
            </button>
            <button
              onClick={onRunBackfill}
              disabled={running}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {running ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  Backfilling…
                </>
              ) : (
                <>
                  <TrendingUp className="w-3.5 h-3.5" />
                  {forceRebuild ? 'Force Rebuild' : 'Fill Missing Rows'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Per-symbol coverage table (expandable) */}
      {coverage && coverage.symbols.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setSymbolsExpanded((v) => !v)}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            {symbolsExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {symbolsExpanded ? 'Hide' : 'Show'} per-symbol coverage ({coverage.symbols.length})
          </button>

          {symbolsExpanded && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-1.5 pr-3 font-semibold text-gray-600">Symbol</th>
                    <th className="text-right py-1.5 px-3 font-semibold text-gray-600">Existing</th>
                    <th className="text-right py-1.5 px-3 font-semibold text-gray-600">Expected</th>
                    <th className="text-right py-1.5 px-3 font-semibold text-gray-600">Missing</th>
                    <th className="text-right py-1.5 px-3 font-semibold text-gray-600">Coverage</th>
                    <th className="text-right py-1.5 pl-3 font-semibold text-gray-600">Last Date</th>
                  </tr>
                </thead>
                <tbody>
                  {coverage.symbols.map((s) => {
                    const color =
                      s.coverage_pct >= 80 ? 'text-teal-700'
                      : s.coverage_pct >= 40 ? 'text-amber-600'
                      : 'text-red-600';
                    return (
                      <tr key={s.symbol} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-1.5 pr-3 font-mono font-semibold text-gray-800">{s.symbol}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums text-gray-600">{s.existing_rows.toLocaleString()}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums text-gray-500">{s.expected_rows.toLocaleString()}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums text-gray-500">{s.missing_rows.toLocaleString()}</td>
                        <td className={`py-1.5 px-3 text-right tabular-nums font-semibold ${color}`}>{s.coverage_pct.toFixed(1)}%</td>
                        <td className="py-1.5 pl-3 text-right text-gray-400 font-mono">{s.max_date ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
